import { useCallback, useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash-es';

import { IStoreAddress } from '@rbi-ctg/store';
import { usePrevious } from 'hooks/use-previous';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { initStore } from 'utils/preload-store';
import { remapStore } from 'utils/restaurant';

import { IPreloadedStoreState, IStoreContext, IStoreHook, IUseStore, StoreState } from './types';
import { resetLastTimeStoreUpdated } from './use-store-utils';

export * from './types';
export * from './use-any-store';

const preloadedStoreInfo = (): IPreloadedStoreState =>
  LocalStorage.getItem(StorageKeys.STORE) || ({} as IPreloadedStoreState);

export const initialStoreState: StoreState = {
  hasSelection: false,
  selectedUnavailableStoreName: '',
  physicalAddress: {} as IStoreAddress,
  chaseMerchantId: '',
  selectedUnavailableStoreNumber: null
};

/**
 * Hook used for store selection 1.0
 */
export const useStore = (): IUseStore => {
  const { serviceMode } = useServiceModeContext();
  const preloaded = useMemo(() => preloadedStoreInfo(), []);

  // Temporary flag
  // Allows us to select a restaurant when it is unavailable
  const showUnavailableRestaurant = useFlag(
    LaunchDarklyFlag.DISPLAY_SELECTED_RESTAURANT_INFORMATION_WHEN_RESTAURANT_UNAVAILABLE
  );

  /** Explicit state of provider */
  const [storeState, _setStore] = useState<StoreState>(initStore(preloaded, serviceMode));
  const store = useMemo(() => remapStore(storeState), [storeState]);
  const setStore = useCallback<IStoreContext['setStore']>(
    newStore =>
      _setStore(s => {
        const value = typeof newStore === 'function' ? newStore(s) : newStore;

        if (isEqual(value, s)) {
          return s;
        }

        return value;
      }),
    []
  );

  /** Change store back to initial state (excluding prefetch) */
  const resetStore = useCallback<IStoreContext['resetStore']>(() => setStore(initialStoreState), [
    setStore
  ]);

  const selectUnavailableStore = useCallback(
    ({
      name,
      number,
      physicalAddress
    }: {
      name?: string | null;
      number?: string | null;
      physicalAddress?: IStoreAddress | null;
    }) => {
      setStore({
        ...initialStoreState,
        selectedUnavailableStoreName: name ?? initialStoreState.selectedUnavailableStoreName,
        physicalAddress: physicalAddress ?? initialStoreState.physicalAddress,
        selectedUnavailableStoreNumber: number ?? initialStoreState.selectedUnavailableStoreNumber
      });
    },
    [setStore]
  );

  const onConfirmStoreChange: IStoreHook['onConfirmStoreChange'] = useCallback(
    async ({ newStore, callback }) => {
      setStore({
        ...newStore,
        hasSelection: true
      });

      resetLastTimeStoreUpdated();
      if (typeof callback === 'function') {
        await callback();
      }
    },
    [setStore]
  );

  const previousShowUnavailableRestaurant = usePrevious(showUnavailableRestaurant);
  useEffect(() => {
    if (
      // Reset store when the LD flag changes to false
      previousShowUnavailableRestaurant !== showUnavailableRestaurant &&
      !showUnavailableRestaurant &&
      // And unavailable store is selected
      'selectedUnavailableStoreNumber' in store &&
      !!store.selectedUnavailableStoreNumber
    ) {
      resetStore();
    }
  }, [previousShowUnavailableRestaurant, resetStore, showUnavailableRestaurant, store]);

  useEffect(() => {
    LocalStorage.setItem(StorageKeys.STORE, {
      store
    });
  }, [store]);

  return useMemo(
    () => ({
      resetStore,
      setStore,
      store,
      selectUnavailableStore,
      onConfirmStoreChange
    }),
    [onConfirmStoreChange, resetStore, selectUnavailableStore, setStore, store]
  );
};
